<script setup lang="ts">
/**
 * Banner — Pajamas-styled inline notice supporting four variants. Mirrors
 * the visual states of the Pajamas `GlAlert` component.
 */
export type BannerVariant = 'info' | 'success' | 'warning' | 'danger';

defineProps<{
  variant?: BannerVariant;
  title?: string;
  dismissible?: boolean;
}>();

const emit = defineEmits<{
  (e: 'dismiss'): void;
}>();

function dismiss(): void {
  emit('dismiss');
}
</script>

<template>
  <div
    :class="['cwc-banner', `cwc-banner--${variant ?? 'info'}`]"
    role="status"
    :aria-live="(variant === 'danger') ? 'assertive' : 'polite'"
  >
    <div class="cwc-banner__content">
      <p v-if="title" class="cwc-banner__title">{{ title }}</p>
      <div class="cwc-banner__body"><slot /></div>
    </div>
    <button
      v-if="dismissible"
      type="button"
      class="cwc-banner__close"
      aria-label="Dismiss"
      @click="dismiss"
    >
&times;
</button>
  </div>
</template>

<style scoped>
.cwc-banner {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--gl-spacing-3);
  padding: var(--gl-spacing-3) var(--gl-spacing-4);
  border-radius: var(--gl-border-radius-base);
  border: var(--gl-border-width) solid transparent;
}

.cwc-banner--info    { background: var(--gl-status-info-bg);    color: var(--gl-status-info-fg); }
.cwc-banner--success { background: var(--gl-status-success-bg); color: var(--gl-status-success-fg); }
.cwc-banner--warning { background: var(--gl-status-warning-bg); color: var(--gl-status-warning-fg); }
.cwc-banner--danger  { background: var(--gl-status-danger-bg);  color: var(--gl-status-danger-fg); }

.cwc-banner__title {
  margin: 0 0 var(--gl-spacing-1);
  font-weight: var(--gl-font-weight-bold);
}

.cwc-banner__body {
  font-size: var(--gl-font-size-md);
}

.cwc-banner__close {
  appearance: none;
  background: transparent;
  border: 0;
  color: inherit;
  font-size: var(--gl-font-size-xl);
  line-height: 1;
  padding: 0 var(--gl-spacing-1);
  cursor: pointer;
}
</style>
